import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import { assets, offer, whatsappPrefill } from "@/config/site";
import { RMark } from "./RMark";
import { EASE } from "./Reveal";

const DEADLINE_KEY = "rz_offer_deadline";
const DISMISS_KEY = "rz_offer_dismissed";

const format = (ms) => {
    const s = Math.floor(ms / 1000);
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    const p = (n) => String(n).padStart(2, "0");
    return h > 0 ? `${p(h)}:${p(m)}:${p(sec)}` : `${p(m)}:${p(sec)}`;
};

export const OfferPopup = () => {
    const [show, setShow] = useState(false);
    const [left, setLeft] = useState(null);

    const close = () => {
        sessionStorage.setItem(DISMISS_KEY, "1");
        setShow(false);
    };

    useEffect(() => {
        if (sessionStorage.getItem(DISMISS_KEY)) return undefined;
        const t = setTimeout(() => setShow(true), 3500);
        return () => clearTimeout(t);
    }, []);

    useEffect(() => {
        if (!show) return undefined;
        let deadline = Number(localStorage.getItem(DEADLINE_KEY));
        if (!deadline || deadline < Date.now()) {
            deadline = Date.now() + offer.campaignSeconds * 1000;
            localStorage.setItem(DEADLINE_KEY, String(deadline));
        }
        const tick = () => setLeft(Math.max(0, deadline - Date.now()));
        tick();
        const iv = setInterval(tick, 1000);
        document.body.style.overflow = "hidden";
        const onKey = (e) => e.key === "Escape" && close();
        document.addEventListener("keydown", onKey);
        return () => {
            clearInterval(iv);
            document.body.style.overflow = "";
            document.removeEventListener("keydown", onKey);
        };
    }, [show]);

    return (
        <AnimatePresence>
            {show && (
                <motion.div
                    data-testid="offer-popup"
                    role="dialog"
                    aria-modal="true"
                    aria-label="Exclusive limited-time offer"
                    className="fixed inset-0 z-[90] overflow-y-auto"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.45, ease: EASE }}
                >
                    <img
                        src={assets.facility}
                        alt=""
                        aria-hidden="true"
                        className="absolute inset-0 h-full w-full object-cover"
                    />
                    <div className="absolute inset-0 bg-rz-black/92" />
                    <div className="rz-pattern absolute inset-0 opacity-60" />

                    <button
                        data-testid="offer-popup-close"
                        onClick={close}
                        aria-label="Close offer"
                        className="absolute right-5 top-5 z-10 border border-rz-steel bg-rz-black/60 p-2.5 text-rz-white transition-colors hover:border-rz-red"
                    >
                        <X size={18} />
                    </button>

                    <div className="relative flex min-h-full flex-col items-center justify-center px-5 py-16 text-center">
                        <motion.img
                            src={assets.emblemTransparent}
                            alt="Revautoz"
                            className="h-14 w-14 object-contain sm:h-16 sm:w-16"
                            initial={{ opacity: 0, scale: 0.7 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.6, delay: 0.15, ease: EASE }}
                        />
                        <motion.div
                            initial={{ opacity: 0, y: 16 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: 0.3, ease: EASE }}
                            className="mt-7 flex items-center gap-3"
                        >
                            <RMark className="h-5 w-5 text-rz-white" />
                            <span className="font-display text-[11px] uppercase tracking-[0.35em] text-rz-smoke">
                                Exclusive limited-time offer
                            </span>
                            <RMark className="h-5 w-5 text-rz-white" />
                        </motion.div>

                        <motion.p
                            initial={{ opacity: 0, scale: 0.92 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.7, delay: 0.45, ease: EASE }}
                            className="relative mt-5 font-display text-[26vw] font-bold uppercase leading-none tracking-tight text-rz-red sm:text-[10rem]"
                            style={{ textShadow: "0 0 80px rgba(225,6,0,0.35)" }}
                        >
                            25% off
                        </motion.p>

                        <motion.p
                            initial={{ opacity: 0, y: 14 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: 0.6, ease: EASE }}
                            className="mt-3 font-display text-lg font-semibold uppercase tracking-[0.2em] text-rz-white sm:text-xl"
                        >
                            <span className="text-rz-red">+</span> Free inspection
                        </motion.p>

                        <motion.div
                            initial={{ opacity: 0, y: 14 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: 0.72, ease: EASE }}
                        >
                            <p className="mt-4 max-w-md text-sm leading-relaxed text-rz-smoke">
                                Your vehicle deserves more than ordinary care. Claim your exclusive
                                Revautoz offer before it expires.
                            </p>
                            <div className="mt-7 inline-block border border-rz-steel bg-rz-black/70 px-8 py-4">
                                <p className="font-display text-[9px] uppercase tracking-[0.35em] text-rz-smoke">
                                    Offer expires in
                                </p>
                                <p
                                    data-testid="offer-popup-countdown"
                                    className="mt-2 font-display text-4xl font-bold tracking-[0.12em] text-rz-white tabular-nums"
                                >
                                    {left === null ? "--:--" : format(left)}
                                </p>
                            </div>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, y: 14 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: 0.85, ease: EASE }}
                            className="mt-8 w-full max-w-sm"
                        >
                            <a
                                data-testid="offer-popup-cta"
                                href={whatsappPrefill(offer.exclusiveMessage)}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={close}
                                className="btn-sweep block w-full bg-rz-red px-8 py-4.5 font-display text-[14px] font-bold uppercase tracking-[0.18em] text-white transition-colors duration-300 hover:bg-rz-reddeep"
                                style={{ paddingTop: 18, paddingBottom: 18 }}
                            >
                                Claim 25% offer
                            </a>
                            <p className="mt-3 font-display text-[10px] uppercase tracking-[0.25em] text-rz-white/80">
                                + Free vehicle inspection
                            </p>
                            <button
                                data-testid="offer-popup-skip"
                                onClick={close}
                                className="mt-6 font-display text-[11px] uppercase tracking-[0.22em] text-rz-smoke underline decoration-rz-steel underline-offset-4 transition-colors hover:text-rz-white"
                            >
                                Proceed without exclusive offer
                            </button>
                        </motion.div>
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};
